import * as THREE from 'three/webgpu';
import {
	computeDirectionArrowWorldSizeFromPixels,
	PatternDirectionArrowObject,
} from '../../manager/directionArrow';
import type {
	SketchRotateCenterAxisConfig,
	SketchRotateCenterAxisDirection,
	SketchRotateCenterAxisHelperFrame,
} from '../../../types/sketch/entities-transform/SketchRotateCenterAxisHelper';

export type { SketchRotateCenterAxisHelperFrame } from '../../../types/sketch/entities-transform/SketchRotateCenterAxisHelper';

//------ 旋转中心三轴辅助对象 开始------
/** 旋转中心辅助轴方向常量。 */
const SKETCH_ROTATE_CENTER_AXIS_DIRECTION = {
	/** 草图参考面 X 轴方向。 */
	X: 'x',
	/** 草图参考面 Y 轴方向。 */
	Y: 'y',
	/** 草图参考面法向方向。 */
	Z: 'z',
} as const;

/**
 * 旋转中心三轴辅助对象。
 *
 * 设计说明：
 * 1. 该对象只表达 Rotate Entities 命令态的临时视觉辅助，不进入草图组、历史或拾取链路；
 * 2. 三个箭头分别沿当前草图参考面的 X、Y、法向方向显示；
 * 3. 通过 `computeDirectionArrowWorldSizeFromPixels` 把固定屏幕像素尺寸换算为世界尺寸，保持视口缩放时视觉大小稳定；
 * 4. 资源释放由 `dispose()` 统一完成，避免命令反复进入时泄漏 geometry / material。
 */
export class SketchRotateCenterAxisHelper extends THREE.Group {
	/** 运行时类型标记，供选择链路过滤。 */
	public readonly isSketchRotateCenterAxisHelper = true;

	/** 跳过常规选择链，避免辅助对象被误选中。 */
	public readonly skipSelection = true;

	/** 默认屏幕像素长度。 */
	private static readonly DEFAULT_PIXEL_SIZE = 76;

	/** 辅助轴统一颜色，保持与 SolidWorks 草图命令辅助标记接近。 */
	private static readonly AXIS_COLOR = 0x4aa3ff;

	/** 中心点网格。 */
	private readonly centerMesh: THREE.Mesh;

	/** 三轴箭头配置集合。 */
	private readonly axisConfigs: SketchRotateCenterAxisConfig[];

	/** 当前旋转中心世界坐标。 */
	private readonly centerWorld = new THREE.Vector3();

	/** 箭头局部锚点复用向量，避免渲染前同步时重复创建临时对象。 */
	private readonly localAnchorWorld = new THREE.Vector3();

	/** 读取当前相机的回调。 */
	private cameraProvider: (() => THREE.Camera | null | undefined) | null = null;

	/** 读取当前视口容器的回调。 */
	private containerProvider: (() => HTMLElement | null | undefined) | null = null;

	/**
	 * 创建旋转中心三轴辅助对象。
	 */
	public constructor() {
		super();
		this.name = '旋转实体-旋转中心三轴辅助对象';
		this.renderOrder = 90;

		this.centerMesh = this.createCenterMesh();
		this.axisConfigs = [
			this.createAxisConfig(SKETCH_ROTATE_CENTER_AXIS_DIRECTION.X),
			this.createAxisConfig(SKETCH_ROTATE_CENTER_AXIS_DIRECTION.Y),
			this.createAxisConfig(SKETCH_ROTATE_CENTER_AXIS_DIRECTION.Z),
		];

		this.add(this.centerMesh);
		this.axisConfigs.forEach((config) => {
			this.add(config.arrow);
		});
		this.installScreenSizeSyncHook();
	}

	/**
	 * 绑定屏幕尺寸同步上下文。
	 *
	 * @param cameraProvider 当前相机读取回调。
	 * @param containerProvider 当前视口容器读取回调。
	 */
	public bindScreenSizeContext(
		cameraProvider: () => THREE.Camera | null | undefined,
		containerProvider: () => HTMLElement | null | undefined,
	): void {
		this.cameraProvider = cameraProvider;
		this.containerProvider = containerProvider;
		this.syncScreenSize(cameraProvider(), containerProvider());
	}

	/**
	 * 更新三轴辅助对象所在坐标系。
	 *
	 * @param frame 旋转中心与参考面坐标系。
	 */
	public updateFrame(frame: SketchRotateCenterAxisHelperFrame): void {
		this.centerWorld.copy(frame.centerWorld);
		this.position.copy(this.centerWorld);

		this.updateAxisDirection(SKETCH_ROTATE_CENTER_AXIS_DIRECTION.X, frame.xAxisWorld);
		this.updateAxisDirection(SKETCH_ROTATE_CENTER_AXIS_DIRECTION.Y, frame.yAxisWorld);
		this.updateAxisDirection(SKETCH_ROTATE_CENTER_AXIS_DIRECTION.Z, frame.zAxisWorld);
		this.syncScreenSize(this.cameraProvider?.(), this.containerProvider?.());
	}

	/**
	 * 同步辅助对象屏幕空间尺寸。
	 *
	 * @param camera 当前相机。
	 * @param container 当前视口容器。
	 */
	public syncScreenSize(
		camera: THREE.Camera | null | undefined,
		container: HTMLElement | null | undefined,
	): void {
		const worldSize = computeDirectionArrowWorldSizeFromPixels(
			camera,
			container,
			this.centerWorld,
			SketchRotateCenterAxisHelper.DEFAULT_PIXEL_SIZE,
		);

		const shaftLength = worldSize * 0.48;
		const headLength = worldSize * 0.16;
		const shaftRadius = worldSize * 0.018;
		const headRadius = worldSize * 0.055;
		const centerRadius = worldSize * 0.055;
		this.centerMesh.scale.setScalar(centerRadius);
		this.axisConfigs.forEach((config) => {
			config.arrow.updateVisual({
				anchorWorld: this.localAnchorWorld,
				directionWorld: config.worldDirection,
				shaftRadius,
				shaftLength,
				headRadius,
				headLength,
				bottomLength: 0,
				topLength: shaftLength,
				anchorLift: 0,
			});
		});
	}

	/**
	 * 释放辅助对象资源。
	 */
	public dispose(): void {
		this.disposeMesh(this.centerMesh);
		this.axisConfigs.forEach((config) => {
			config.arrow.dispose();
		});
	}

	/**
	 * 创建单个辅助轴配置。
	 *
	 * @param direction 轴方向标识。
	 * @returns 辅助轴配置。
	 */
	private createAxisConfig(direction: SketchRotateCenterAxisDirection): SketchRotateCenterAxisConfig {
		return {
			direction,
			arrow: new PatternDirectionArrowObject(SketchRotateCenterAxisHelper.AXIS_COLOR),
			worldDirection: new THREE.Vector3(1, 0, 0),
		};
	}

	/**
	 * 更新指定辅助轴方向。
	 *
	 * @param direction 轴方向标识。
	 * @param worldDirection 世界方向。
	 */
	private updateAxisDirection(
		direction: SketchRotateCenterAxisDirection,
		worldDirection: THREE.Vector3,
	): void {
		const config = this.axisConfigs.find((item) => item.direction === direction);
		if (!config) {
			return;
		}
		if (worldDirection.lengthSq() <= 1e-12) {
			return;
		}
		config.worldDirection.copy(worldDirection).normalize();
	}

	/**
	 * 创建中心点网格。
	 *
	 * @returns 中心点网格。
	 */
	private createCenterMesh(): THREE.Mesh {
		const geometry = new THREE.SphereGeometry(1, 18, 12);
		const material = new THREE.MeshBasicMaterial({
			color: SketchRotateCenterAxisHelper.AXIS_COLOR,
			depthTest: false,
			depthWrite: false,
			transparent: true,
			opacity: 0.92,
		});
		const mesh = new THREE.Mesh(geometry, material);
		mesh.renderOrder = 91;
		return mesh;
	}

	/**
	 * 安装渲染前屏幕尺寸同步钩子。
	 */
	private installScreenSizeSyncHook(): void {
		this.centerMesh.onBeforeRender = (): void => {
			this.syncScreenSize(this.cameraProvider?.(), this.containerProvider?.());
		};
	}

	/**
	 * 释放单个网格资源。
	 *
	 * @param mesh 待释放网格。
	 */
	private disposeMesh(mesh: THREE.Mesh): void {
		mesh.geometry.dispose();
		const material = mesh.material;
		if (Array.isArray(material)) {
			material.forEach((item) => item.dispose());
			return;
		}
		material.dispose();
	}
}
//------ 旋转中心三轴辅助对象 结束------
